import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE } from "@/components/shared";
import { IMAGES, waLink, CREA } from "@/lib/site";

const LINES = [
  { text: "SUA EDIFICAÇÃO", gold: false },
  { text: "MERECE MAIS DO QUE", gold: false },
  { text: "UMA REFORMA.", gold: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="inicio" ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Edifício moderno de alto padrão ao anoitecer — engenharia predial Realiza Engenharia"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-navy-950/35" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/70" />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="mb-7 flex items-center gap-4"
        >
          <span className="h-px w-14 bg-gold" />
          <p className="font-mono text-[10px] uppercase tracking-[0.34em] text-gold sm:text-[11px]">
            Realiza Engenharia — Inteligência em Gestão Técnica Condominial
          </p>
        </motion.div>

        <h1 className="font-display text-[15vw] font-extrabold uppercase leading-[0.94] tracking-wide text-white sm:text-7xl lg:text-8xl xl:text-[6.6rem]">
          {LINES.map((line, i) => (
            <span key={line.text} className="mask-line">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.15, ease: EASE }}
                className={line.gold ? "text-gold-grad" : ""}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 sm:text-xl"
        >
          Merece engenharia, planejamento, segurança e decisões técnicas.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.08, ease: EASE }}
          className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400"
        >
          Soluções completas em engenharia, manutenção predial, recuperação, modernização e gestão
          técnica para condomínios, empresas, administradoras e gestores que buscam segurança,
          qualidade e valorização patrimonial.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <a
            href="#orcamento"
            data-testid="hero-cta-orcamento"
            className="group relative inline-flex items-center justify-center rounded-full bg-gold px-8 py-4 font-display text-base font-bold tracking-[0.14em] text-navy-950 transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_36px_rgba(212,175,55,0.4)]"
          >
            SOLICITAR ORÇAMENTO
          </a>
          <a
            href={waLink("Olá! Gostaria de falar com um engenheiro da Realiza Engenharia.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-engenheiro"
            className="inline-flex items-center justify-center rounded-full border border-gold/50 px-8 py-4 font-display text-base font-bold tracking-[0.14em] text-gold transition-all duration-300 hover:bg-gold/10"
          >
            FALAR COM UM ENGENHEIRO
          </a>
          <a
            href="#plano-realiza"
            data-testid="hero-cta-plano"
            className="group inline-flex items-center gap-2 px-2 py-4 font-display text-base font-semibold tracking-[0.14em] text-slate-300 transition-colors hover:text-gold"
          >
            CONHECER O PLANO REALIZA
            <span className="h-px w-6 bg-gold transition-all duration-300 group-hover:w-10" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-7 left-5 z-10 flex items-center gap-4 lg:left-8"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/30">
          <ArrowDown className="h-4 w-4 animate-bounce text-gold" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
          {CREA} — Porto Alegre/RS
        </span>
      </motion.div>
    </section>
  );
}
